import { Input } from 'antd';
import { useState } from 'react';
import { useLoggedInUser } from '../../../hooks/useLoggedInUser';
import useQueryTeamRoles, {
  teamRolesQueryName,
} from '../../../hooks/useQueryTeamRoles';
import SaveAndDeleteButtons from '../../SaveAndDeleteButtons';
import useCreateTeamRole from '../../../hooks/useCreateTeamRole';
import { RoleGroup, roleGroupTypes } from '../types';
import Select from '../../AccessibleComponents/Select';
import rfsTheme from '../../../theme';
import { roleNameMaxLength } from '../team-validation';
import PageLoadingSpinner from '../../PageLoadingSpinner';
import { errorMessage, successMessage } from '../../../lib/utils/messages';
import { onApolloError } from '../../../lib/graphql/helpers';
import { useNotify } from '../../../hooks/useNotify';

type RoleCreationFormProps = {
  readonly dismissModal: () => void;
};

/*
 * Renders an Input box for a user to add a new team role option.
 */
const CreateTeamRoleForm = ({ dismissModal }: RoleCreationFormProps) => {
  const { notify } = useNotify();
  const { loggedInUser } = useLoggedInUser();

  const [newRole, setNewRole] = useState('');
  const [roleGroup, setRoleGroup] = useState<RoleGroup | undefined>(undefined);

  const [insertRole] = useCreateTeamRole(onApolloError(notify.error));

  const { data: allTeamRoles } = useQueryTeamRoles(onApolloError(notify.error));

  if (loggedInUser === undefined) {
    return <PageLoadingSpinner />;
  }

  const onSaveNewRole = () => {
    void insertRole({
      variables: {
        name: newRole,
        role_group: roleGroup,
      },
      onCompleted: () => {
        setNewRole('');
        notify.success(successMessage.roleAdded);
      },
      refetchQueries: [teamRolesQueryName],
    });

    dismissModal();
  };

  const newRoleAlreadyExists =
    allTeamRoles?.team_role.some(
      (role) => role.name.toLowerCase() === newRole.toLowerCase()
    ) ?? false;

  const roleNameIsTooLong = newRole.length > roleNameMaxLength;

  return (
    <>
      <label htmlFor="new-role-input">Role Name</label>
      <Input
        id={'new-role-input'}
        value={newRole}
        onInput={(e) => setNewRole(e.currentTarget.value)}
        aria-label="New Role Input"
      />
      <label htmlFor="create-team-role-form-input">Role Group</label>
      <Select
        id="create-team-role-form-input"
        className="role-group-select"
        onChange={(v) => {
          setRoleGroup(
            v.currentTarget.value === '' ? undefined : v.currentTarget.value
          );
        }}
        value={roleGroup}
        options={roleGroupTypes.filter((group) => group !== 'SOC')}
        title={'Role Group'}
        showEmptyOption={true}
        style={{ height: '32px' }}
      />
      <div style={{ marginTop: '20px', marginLeft: '90%' }}>
        <SaveAndDeleteButtons
          roleId="new"
          onSave={onSaveNewRole}
          saveButtonDisabled={[
            newRole === '',
            newRoleAlreadyExists,
            roleNameIsTooLong,
          ].some((b) => b)}
          loading={false}
          onDelete={() => 0}
          showDelete={false}
          routeOnDelete=""
          showSave
        />
      </div>

      {newRoleAlreadyExists && newRole !== '' && (
        <p style={{ color: rfsTheme.errorTextColour }}>
          {errorMessage.roleAlreadyExists}
        </p>
      )}

      {roleNameIsTooLong && (
        <p style={{ color: rfsTheme.errorTextColour }}>
          {errorMessage.roleNameTooLong}
        </p>
      )}
    </>
  );
};

export default CreateTeamRoleForm;
